import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useUser } from "../../../../pages/Home/UserContext";
import { getSettings } from "../../../../services/settingsServices";
import { getAllMessagesContactForm } from "../../../../services/contactformServices";
import { getPageMeta } from "../data/adminShellConfig";

export default function useAdminTopbar() {
  const { user, updateUser } = useUser();
  const navigate = useNavigate();
  const location = useLocation();

  const [mounted, setMounted] = useState(false);
  const [settings, setSettings] = useState(null);
  const [unreadContactCount, setUnreadContactCount] = useState(0);
  const [theme, setTheme] = useState(() => {
    if (typeof document === "undefined") return "light";
    return document.documentElement.classList.contains("dark")
      ? "dark"
      : "light";
  });

  useEffect(() => {
    setMounted(true);

    const storedUserInfo = localStorage.getItem("userInfo");

    if (storedUserInfo) {
      try {
        const parsed = JSON.parse(storedUserInfo);

        if (parsed && !user) {
          updateUser(parsed);
        }
      } catch (error) {
        console.error(
          "Erreur lors du parsing des informations de l'utilisateur",
          error,
        );
      }
    }
  }, [user, updateUser]);

  useEffect(() => {
    let active = true;

    const loadSettings = async () => {
      try {
        const data = await getSettings();

        if (active) {
          setSettings(Array.isArray(data) ? data[0] : data);
        }
      } catch (error) {
        console.error("Erreur chargement settings :", error);
      }
    };

    loadSettings();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;

    const loadUnreadContactCount = async () => {
      try {
        const data = await getAllMessagesContactForm();
        const messages = Array.isArray(data) ? data : [];
        const unread = messages.filter((m) => !m.read_message).length;

        if (active) {
          setUnreadContactCount(unread);
        }
      } catch (error) {
        console.error(
          "Erreur lors de la récupération des messages de contact :",
          error,
        );
      }
    };

    loadUnreadContactCount();

    const interval = setInterval(loadUnreadContactCount, 60000);

    return () => {
      active = false;
      clearInterval(interval);
    };
  }, []);

  const pageMeta = useMemo(
    () => getPageMeta(location.pathname),
    [location.pathname],
  );

  const ville = useMemo(() => {
    return (
      settings?.nom_mairie?.trim() ||
      settings?.ville?.trim() ||
      "MonAppliMairie"
    );
  }, [settings]);

  const logoSrc = useMemo(() => {
    const logoMairie = settings?.logo_mairie?.trim();
    return logoMairie || "/img/Logo_MAM_fondtransparent.png";
  }, [settings]);

  const avatarSrc = useMemo(() => {
    return `${user?.photoURL || "/img/user_avatar.jpg"}?v=${
      user?.photoURL || "default"
    }`;
  }, [user?.photoURL]);

  const isLogged = Boolean(user?.userId || user?.id);

  const handleLogout = () => {
    localStorage.removeItem("userInfo");
    localStorage.removeItem("token");
    updateUser(null);
    navigate("/login");
  };

  const toggleTheme = () => {
    const nextTheme = document.documentElement.classList.toggle("dark")
      ? "dark"
      : "light";

    localStorage.setItem("theme", nextTheme);
    setTheme(nextTheme);
  };

  const goToPublicSite = () => navigate("/");
  const goToDashboard = () => navigate("/appli/dashboard");
  const goToContactMessages = () =>
    navigate("/appli/dashboard#messages-contact");

  return {
    user,
    mounted,
    theme,
    settings,
    pageMeta,
    ville,
    logoSrc,
    avatarSrc,
    isLogged,
    unreadContactCount,
    handleLogout,
    toggleTheme,
    goToPublicSite,
    goToDashboard,
    goToContactMessages,
  };
}
